import { useState, useMemo } from 'react';
import { MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { DataTable, EmptyState, Pagination, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, IconButton,
} from '@vnetwork/ui/primitives';
import { Toolbar } from '@vnetwork/ui/layout';
import { formatDate } from '@/lib/format';
import {
  useCommissionPolicies,
  useDeleteCommissionPolicy,
  type CommissionPolicy,
} from '@/api/commission-policies';
import { paginationView } from '@/pages/customers/pagination-summary';
import { scopeTone, humanize } from './status-tones';
import { useAffiliatePolicyPermissions } from '../use-affiliate-policy-permissions';
import { PolicyFormDrawer } from '../commission-policies/policy-form-drawer';

const PAGE_SIZE = 20;

interface CommissionPoliciesTabProps {
  affiliateId: number;
}

/**
 * Commission policies tab — paginated list of policies for this affiliate with
 * create/edit (PolicyFormDrawer) and delete (ConfirmDialog), gated by
 * canCreatePolicy / canUpdatePolicy / canDeletePolicy from useAffiliatePolicyPermissions.
 *
 * Mirrors CommissionPoliciesPage pattern but scoped via affiliate_id param.
 * The PolicyFormDrawer already handles affiliate_ids via the owner picker —
 * no fork of the drawer was needed.
 */
export function CommissionPoliciesTab({ affiliateId }: CommissionPoliciesTabProps) {
  const toast = useToast();
  const perms = useAffiliatePolicyPermissions();

  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<CommissionPolicy | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CommissionPolicy | null>(null);

  const { data, isLoading, isError, refetch } = useCommissionPolicies({
    affiliate_id: affiliateId,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  const deleteMutation = useDeleteCommissionPolicy(deleteTarget?.policy_id ?? 0);

  const openCreate = () => {
    setEditingPolicy(null);
    setDrawerOpen(true);
  };

  const openEdit = (policy: CommissionPolicy) => {
    setEditingPolicy(policy);
    setDrawerOpen(true);
  };

  const handleDrawerClose = (open: boolean) => {
    setDrawerOpen(open);
    if (!open) setEditingPolicy(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync();
      toast.show({ tone: 'success', title: 'Policy deleted', description: deleteTarget.policy_name });
      setDeleteTarget(null);
    } catch (err) {
      toast.show({
        tone: 'danger',
        title: 'Delete failed',
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    }
  };

  const columns = useMemo<ColumnDef<CommissionPolicy, unknown>[]>(
    () => [
      {
        id: 'policy_name',
        header: 'Policy Name',
        cell: ({ row }) => row.original.policy_name,
      },
      {
        id: 'scope',
        header: 'Scope',
        cell: ({ row }) => (
          <StatusPill tone={scopeTone(row.original.scope)}>
            {humanize(row.original.scope)}
          </StatusPill>
        ),
      },
      {
        id: 'product',
        header: 'Product / Group',
        cell: ({ row }) => {
          const { product_name, product_group_name } = row.original;
          return product_name || product_group_name || '—';
        },
      },
      {
        id: 'commission_percent',
        header: 'Rate',
        cell: ({ row }) => `${row.original.commission_percent}%`,
      },
      {
        id: 'priority',
        header: 'Priority',
        cell: ({ row }) => row.original.priority,
      },
      {
        id: 'is_active',
        header: 'Active',
        cell: ({ row }) =>
          row.original.is_active ? (
            <StatusPill tone="success">Active</StatusPill>
          ) : (
            <StatusPill tone="neutral">Inactive</StatusPill>
          ),
      },
      {
        id: 'effective_date',
        header: 'Effective',
        cell: ({ row }) => formatDate(row.original.effective_date),
      },
      {
        id: 'expired_date',
        header: 'Expires',
        cell: ({ row }) => formatDate(row.original.expired_date),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const policy = row.original;
          if (!perms.canUpdatePolicy && !perms.canDeletePolicy) return null;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton variant="ghost" size="sm" aria-label="Policy actions">
                  <MoreHorizontal size={16} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {perms.canUpdatePolicy && (
                  <DropdownMenuItem onSelect={() => openEdit(policy)}>
                    <Pencil size={14} />
                    Edit
                  </DropdownMenuItem>
                )}
                {perms.canDeletePolicy && (
                  <DropdownMenuItem onSelect={() => setDeleteTarget(policy)} tone="danger">
                    <Trash2 size={14} />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [perms.canUpdatePolicy, perms.canDeletePolicy],
  );

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load commission policies"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <>
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {perms.canCreatePolicy && (
          <Toolbar
            style={{ marginBottom: 0 }}
            end={
              <Button startIcon={<Plus size={15} />} onClick={openCreate}>
                New policy
              </Button>
            }
          />
        )}

        <DataTable
          columns={columns}
          data={rows}
          enableSorting={false}
          getRowId={(r) => String(r.policy_id)}
          emptyState={
            <EmptyState
              title="No commission policies"
              description="Commission policies assigned to this affiliate will appear here."
            />
          }
          footer={
            isLoading ? null : (
              <Pagination
                page={page}
                pageCount={view.pageCount}
                onPageChange={setPage}
                summary={view.summary}
              />
            )
          }
        />
      </div>

      <PolicyFormDrawer
        open={drawerOpen}
        onOpenChange={handleDrawerClose}
        policy={editingPolicy}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete commission policy"
        description={`Are you sure you want to delete "${deleteTarget?.policy_name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
